import { useState, useEffect } from "react";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import SoftDuplicateAlert from "./invoices/SoftDuplicateAlert";
import AiUploadTab from "./invoices/AiUploadTab";
import InvoiceFormFields from "./invoices/InvoiceFormFields";
import OwnCifOverrideModal from "./invoices/OwnCifOverrideModal";

/**
 * Modal principal para crear una factura/ingreso/salario.
 * Orquesta 3 subcomponentes:
 *   - `AiUploadTab`         → subida de fichero + extracción OCR (IA).
 *   - `InvoiceFormFields`   → rejilla compartida de campos del formulario.
 *   - `SoftDuplicateAlert`  → aviso inline de posible duplicado tras guardar.
 *
 * La lógica de red (upload, extract, save, resolución de duplicados) y el
 * estado del formulario viven aquí para mantener un único origen de verdad.
 */
export default function UploadInvoiceModal({
  open,
  onOpenChange,
  categories,
  onCreated,
  onCategoriesChange,
  defaultType = "expense",
  defaultCategoryKind = null,
  prefill = null,
}) {
  const { t, lang } = useI18n();

  // ---- Labels contextuales (income / salary / expense) ----
  const isIncome = defaultType === "income";
  const isSalary = defaultType === "expense" && defaultCategoryKind === "salary";
  const modalTitle = isIncome
    ? t("uploadIncome")
    : isSalary
      ? t("uploadSalary")
      : t("uploadInvoice");
  const vendorLabel = isIncome || isSalary ? t("concept") : t("vendor");

  const initialCategory = () => {
    if (defaultCategoryKind) {
      const match = categories.find((c) => c.kind === defaultCategoryKind);
      if (match) return match.name;
    }
    return "other";
  };

  // ---- State ----
  const [tab, setTab] = useState("ai");
  const [file, setFile] = useState(null);
  const [fileId, setFileId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    vendor: "",
    vendor_tax_id: "",
    date: new Date().toISOString().slice(0, 10),
    amount: "",
    subtotal: "",
    tax_rate: "21",
    currency: "EUR",
    category: initialCategory(),
    type: defaultType,
    is_credit_note: false,
    invoice_number: "",
    tax: "",
    notes: "",
    iva_breakdown: null,
    recurring: false,
    repeat_until: "",
  });

  // Soft-duplicate: rellenado cuando POST /invoices devuelve `soft_duplicate`.
  const [softDupInfo, setSoftDupInfo] = useState(null);
  const [softDupBusy, setSoftDupBusy] = useState(false);
  const [softDupDateDraft, setSoftDupDateDraft] = useState("");

  // Iter 82 (Bloque 3.j) — modal "Editar proveedor" cuando el OCR extrajo el
  // CIF propio y el backend devuelve 409 `own_invoice`.
  const [ownCifOpen, setOwnCifOpen] = useState(false);
  const [ownCifDetected, setOwnCifDetected] = useState(null);

  // Reset al abrir
  useEffect(() => {
    if (!open) return;
    const isSalaryDefault = defaultType === "expense" && defaultCategoryKind === "salary";
    setFile(null);
    setFileId(null);
    setSoftDupInfo(null);
    setSoftDupBusy(false);
    setSoftDupDateDraft("");
    setForm({
      vendor: prefill?.vendor || "",
      vendor_tax_id: prefill?.vendor_tax_id || "",
      date: prefill?.date || new Date().toISOString().slice(0, 10),
      amount: "",
      subtotal: "",
      tax_rate: isSalaryDefault ? "0" : "21",
      currency: "EUR",
      category: initialCategory(),
      type: defaultType,
      is_credit_note: false,
      invoice_number: "",
      tax: isSalaryDefault ? "0" : "",
      notes: "",
      iva_breakdown: null,
      recurring: false,
      repeat_until: "",
    });
    // Si llegamos vía deeplink con proveedor precargado, saltar directo al manual.
    setTab(prefill?.vendor ? "manual" : "ai");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultType, defaultCategoryKind, prefill?.vendor, prefill?.vendor_tax_id, prefill?.date]);

  // ---- Upload + OCR ----
  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    const f = files[0];
    setFile(f);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/invoices/upload-file", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setFileId(data.file_id);
      if (data.duplicate) {
        toast.warning(
          lang === "es"
            ? `Archivo duplicado: ya existe ${data.duplicate_of?.vendor ? `como "${data.duplicate_of.vendor}"` : ""}`
            : `Duplicate file: already exists ${data.duplicate_of?.vendor ? `as "${data.duplicate_of.vendor}"` : ""}`
        );
      } else {
        toast.success(lang === "es" ? "Archivo subido" : "File uploaded");
      }
    } catch (err) {
      toast.error(formatApiError(err));
      setFile(null);
    } finally {
      setUploading(false);
    }
  };

  const extract = async () => {
    if (!fileId) return;
    setExtracting(true);
    try {
      const { data } = await api.post(`/invoices/extract/${fileId}`);
      if (data.error) {
        toast.error(data.error);
        return;
      }
      setForm((prev) => ({
        ...prev,
        vendor: data.vendor || prev.vendor,
        vendor_tax_id: data.vendor_tax_id || prev.vendor_tax_id,
        date: data.date || prev.date,
        amount: data.amount != null ? String(data.amount) : prev.amount,
        subtotal: data.subtotal != null ? String(data.subtotal) : prev.subtotal,
        tax_rate: data.tax_rate != null ? String(data.tax_rate) : prev.tax_rate,
        currency: data.currency || prev.currency,
        category: categories.find((c) => c.name === data.category_guess)?.name || prev.category,
        type: data.type || prev.type,
        is_credit_note: data.is_credit_note != null ? !!data.is_credit_note : prev.is_credit_note,
        invoice_number: data.invoice_number || prev.invoice_number,
        tax: data.tax != null ? String(data.tax) : prev.tax,
        notes: data.notes || prev.notes,
        iva_breakdown:
          Array.isArray(data.iva_breakdown) && data.iva_breakdown.length >= 2
            ? data.iva_breakdown.map((b) => ({
                _uid: crypto.randomUUID(),
                base: String(b.base ?? ""),
                rate: String(b.rate ?? ""),
                tax: String(b.tax ?? ""),
              }))
            : prev.iva_breakdown,
      }));
      if (Array.isArray(data.iva_breakdown) && data.iva_breakdown.length >= 2) {
        toast.info(
          lang === "es"
            ? `Detectados ${data.iva_breakdown.length} tramos de IVA`
            : `Detected ${data.iva_breakdown.length} VAT brackets`
        );
      }
      toast.success(lang === "es" ? "Datos extraídos" : "Data extracted");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setExtracting(false);
    }
  };

  // ---- Save ----
  const save = async (overrides = null) => {
    if (!form.amount) {
      toast.error(lang === "es" ? "El importe es obligatorio" : "Amount is required");
      return;
    }
    setSaving(true);
    try {
      const breakdownPayload =
        Array.isArray(form.iva_breakdown) && form.iva_breakdown.length >= 2
          ? form.iva_breakdown
              .map((b) => ({
                base: parseFloat(b.base) || 0,
                rate: parseFloat(b.rate) || 0,
                tax: parseFloat(b.tax) || 0,
              }))
              .filter((b) => b.base !== 0 || b.tax !== 0)
          : null;
      const payload = {
        ...form,
        // Iter 82 — `overrides` viene del OwnCifOverrideModal cuando el
        // usuario elige un proveedor real tras el 409 own_invoice. Sobrescribe
        // vendor + vendor_tax_id (nunca el resto del formulario).
        ...(overrides || {}),
        amount: parseFloat(form.amount),
        subtotal: form.subtotal ? parseFloat(form.subtotal) : null,
        tax: form.tax ? parseFloat(form.tax) : null,
        tax_rate: form.tax_rate !== "" && form.tax_rate != null ? parseFloat(form.tax_rate) : null,
        iva_breakdown: breakdownPayload && breakdownPayload.length >= 2 ? breakdownPayload : null,
        vendor_tax_id: (overrides && "vendor_tax_id" in overrides)
          ? overrides.vendor_tax_id
          : (form.vendor_tax_id || null),
        is_credit_note: !!form.is_credit_note,
        file_id: fileId,
      };
      // Iter 82 — recurring monthly expense. Client passes `recurring:true` +
      // `repeat_until:"YYYY-MM-DD"` and we hit a dedicated endpoint that
      // creates the base invoice + one copy per month up to that date.
      if (form.recurring && form.repeat_until) {
        // Strip transient UI flags — server rejects unknown keys via Pydantic.
        // eslint-disable-next-line no-unused-vars
        const { recurring, ...rest } = payload;
        const { data: bulk } = await api.post("/invoices/recurring", {
          ...rest,
          repeat_until: form.repeat_until,
        });
        toast.success(
          lang === "es"
            ? `Gasto recurrente creado (${bulk.count} copias)`
            : `Recurring expense created (${bulk.count} copies)`
        );
        onCreated?.();
        onOpenChange(false);
        return;
      }
      // eslint-disable-next-line no-unused-vars
      const { recurring: _r, repeat_until: _u, ...single } = payload;
      const { data: created } = await api.post("/invoices", single);
      if (created?.soft_duplicate) {
        setSoftDupInfo({ created, existing: created.soft_duplicate });
        setSoftDupDateDraft(created.date || "");
        toast.warning(
          lang === "es"
            ? "Posible duplicado. Revisa la advertencia y decide qué hacer."
            : "Possible duplicate. Review the warning and choose an action."
        );
        return;
      }
      toast.success(t("saved"));
      onCreated?.();
      onOpenChange(false);
    } catch (err) {
      // Feature 1: backend rechaza con 409 si el CIF del proveedor coincide
      // con el CIF fiscal del tenant. Iter 82 — abrimos un modal para que
      // el usuario elija otro proveedor en vez de perder el trabajo.
      const detail = err?.response?.data?.detail || "";
      if (err?.response?.status === 409 && typeof detail === "string" && detail.startsWith("own_invoice")) {
        setOwnCifDetected(form.vendor_tax_id || null);
        setOwnCifOpen(true);
      } else {
        toast.error(formatApiError(err));
      }
    } finally {
      setSaving(false);
    }
  };

  // ---- Soft-duplicate resolution ----
  const resolveSoftDupChangeDate = async () => {
    if (!softDupInfo?.created?.id || !softDupDateDraft) return;
    setSoftDupBusy(true);
    try {
      await api.put(`/invoices/${softDupInfo.created.id}`, { date: softDupDateDraft });
      toast.success(
        lang === "es"
          ? `Fecha actualizada a ${softDupDateDraft}`
          : `Date updated to ${softDupDateDraft}`
      );
      onCreated?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSoftDupBusy(false);
    }
  };

  const resolveSoftDupDelete = async () => {
    if (!softDupInfo?.created?.id) return;
    setSoftDupBusy(true);
    try {
      await api.delete(`/invoices/${softDupInfo.created.id}`);
      toast.success(lang === "es" ? "Factura duplicada eliminada" : "Duplicate invoice deleted");
      onCreated?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSoftDupBusy(false);
    }
  };

  const resolveSoftDupKeep = () => {
    toast.success(lang === "es" ? "Factura guardada" : "Invoice saved");
    setSoftDupInfo(null);
    onCreated?.();
    onOpenChange(false);
  };

  // Feature 2 · "Actualizar con la última": sobrescribe la factura existente
  // con los datos de la nueva y borra la nueva. La existente conserva su id
  // (referencias, historial, file_id previo si no vino uno nuevo).
  const resolveSoftDupReplace = async () => {
    const keepId = softDupInfo?.existing?.id;
    const dropId = softDupInfo?.created?.id;
    if (!keepId || !dropId) return;
    setSoftDupBusy(true);
    try {
      await api.post(`/invoices/${keepId}/replace-with/${dropId}`);
      toast.success(
        lang === "es"
          ? "Factura existente actualizada con la última"
          : "Existing invoice updated with the latest data"
      );
      setSoftDupInfo(null);
      onCreated?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSoftDupBusy(false);
    }
  };

  // Feature 2 · "Editar": simplemente cierra el aviso y deja el modal
  // abierto con los datos de la nueva factura para que el usuario ajuste
  // y guarde (PUT en un segundo submit reemplaza los campos).
  const resolveSoftDupEdit = () => {
    setSoftDupInfo(null);
    toast.info(
      lang === "es"
        ? "Ajusta los datos y guarda para actualizar la factura"
        : "Adjust the fields and save to update the invoice"
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="upload-invoice-modal"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">{modalTitle}</DialogTitle>
        </DialogHeader>

        <SoftDuplicateAlert
          info={softDupInfo}
          lang={lang}
          busy={softDupBusy}
          dateDraft={softDupDateDraft}
          onDateDraftChange={setSoftDupDateDraft}
          onChangeDate={resolveSoftDupChangeDate}
          onDelete={resolveSoftDupDelete}
          onKeep={resolveSoftDupKeep}
          onReplace={resolveSoftDupReplace}
          onEdit={resolveSoftDupEdit}
        />

        <Tabs value={tab} onValueChange={setTab} className="mt-2">
          <TabsList className="w-full grid grid-cols-2">
            <TabsTrigger value="ai" data-testid="tab-ai-entry">
              <Sparkles size={14} className="mr-1.5" /> {t("aiEntry")}
            </TabsTrigger>
            <TabsTrigger value="manual" data-testid="tab-manual-entry">
              {t("manualEntry")}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="ai">
            <AiUploadTab
              file={file}
              fileId={fileId}
              uploading={uploading}
              extracting={extracting}
              onFiles={handleFiles}
              onRemove={() => {
                setFile(null);
                setFileId(null);
              }}
              onExtract={extract}
            />
          </TabsContent>

          <TabsContent value="manual" className="space-y-1 mt-4">
            <p className="text-xs text-[var(--text-muted)]">
              {lang === "es"
                ? "Introduce los datos manualmente. Puedes adjuntar un archivo opcionalmente."
                : "Enter data manually. You can optionally attach a file."}
            </p>
          </TabsContent>
        </Tabs>

        <InvoiceFormFields
          form={form}
          setForm={setForm}
          categories={categories}
          isIncome={isIncome}
          isSalary={isSalary}
          vendorLabel={vendorLabel}
          onCategoryCreated={onCategoriesChange}
          allowRecurring
        />

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="cancel-upload-btn">
            {t("cancel")}
          </Button>
          <Button
            onClick={() => save()}
            disabled={saving}
            data-testid="save-invoice-btn"
            className="bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white"
          >
            {saving ? "..." : t("save")}
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Iter 82 (Bloque 3.j) — el usuario decide qué proveedor usar cuando
          el OCR extrajo su propio CIF. Al confirmar reintenta el POST con
          el vendor elegido y actualiza el formulario para que quede
          persistido tras cerrar. */}
      <OwnCifOverrideModal
        open={ownCifOpen}
        onOpenChange={setOwnCifOpen}
        detectedCif={ownCifDetected}
        lang={lang}
        onConfirm={async ({ vendor, vendor_tax_id }) => {
          setForm((prev) => ({ ...prev, vendor, vendor_tax_id: vendor_tax_id || "" }));
          await save({ vendor, vendor_tax_id });
        }}
      />
    </Dialog>
  );
}
